import React, { useMemo, forwardRef, useImperativeHandle, useEffect } from 'react';
import { FormField, FormService } from '@shared/services/FormServices';
import { RateComponentRef } from './FlatRate';
import { SharedFormArray } from '@shared/components/SharedFormArray';
import { PageHeader } from '@shared/components/PageHeader';

interface SlabBasedProps {
  data: any;
  hideHeader?: boolean;
  unit?: string;
}

export const SlabBased = forwardRef<RateComponentRef, SlabBasedProps>(({ data, hideHeader, unit }, ref) => {
  const slabFields: FormField[] = useMemo(
    () => [
      { 
        name: "from", 
        label: "From", 
        type: "number", 
        required: true,
        min: 0,
        validation: { min: { message: "Negative values are not allowed" } },
        placeholder: "0" 
      },
      { 
        name: "to", 
        label: "To", 
        type: "number",
        required: false,
        min: 0,
        validation: { min: { message: "Negative values are not allowed" } },
        placeholder: "∞" 
      },
      { 
        name: "type", 
        label: "Type", 
        type: "select", 
        options: [
          { label: "Variable", value: "variable" }, 
          { label: "Fixed", value: "fixed" }
        ],
        required: true,
      },
      { 
        name: "rate", 
        label: "Rate", 
        type: "decimal", 
        required: true,
        allowNegativeValue: false,
        validation: { min: { message: "Negative rates are not allowed" } },
        placeholder: "0.00" 
      },
      { 
        name: "unit", 
        label: "Unit", 
        type: "text", 
        required: true,
        placeholder: "e.g. kWh",
        disabled: true,
        defaultValue: unit,
      }
    ],
    [unit]
  );

const slabFieldInfo = [
  {
    title: "From",
    description:
      "lower bound of the consumption block (inclusive).",
  },
  {
    title: "To",
    description:
      "upper bound. Click ∞ to make the slab open-ended.",
  },
  {
    title: "Type",
    description:
      "Flat charges a fixed amount once when the block is reached. Variable bills per unit.",
  },
  {
    title: "Rate",
    description:
      "Flat amount in $ for flat slabs, or price-per-unit for variable slabs.",
  },
  {
    title: "Unit",
    description:
      "display unit shown on the bill (e.g. $ flat or $/KL).",
  },
];

  const form = FormService.createForm({
    fields: [
      { name: "slabs", type: "form-array", itemFields: slabFields }
    ],
    mode: "onChange",
    validateOnMount: false,
    defaultValues: (data && Object.keys(data).length > 0) ? data : {
      slabs: [{ from: 0, to: "", type: "variable", rate: 0, unit: unit || "" }]
    },
  });
  
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      if (!data.slabs || data.slabs.length === 0) {
        form.reset({
          ...data,
          slabs: [{ from: 0, to: "", type: "variable", rate: 0, unit: unit || "" }]
        });
      } else {
        form.reset(data);
      }
    }
  }, [data, form]);

  // Update all slabs when the unit prop changes
  useEffect(() => {
    if (unit !== undefined) {
      const currentSlabs = form.getValues("slabs") || [];
      const hasDifferentUnit = currentSlabs.some((slab: any) => slab.unit !== unit);
      if (hasDifferentUnit) {
        const updatedSlabs = currentSlabs.map((slab: any) => ({
          ...slab,
          unit: unit
        }));
        form.reset({
          ...form.getValues(),
          slabs: updatedSlabs
        });
      }
    }
  }, [unit, form]);

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      // Check if the changed field is a 'to' field inside the slabs array
      if (name && name.startsWith("slabs.") && name.endsWith(".to")) {
        const parts = name.split(".");
        const index = parseInt(parts[1], 10);
        
        // Push to the end of the event loop to guarantee state is committed
        setTimeout(() => {
          const livePrevTo = form.getValues(name);
          const slabsArray = form.getValues("slabs") || [];
          
          // If there is a next slab, update its 'from' value
          if (!isNaN(index) && slabsArray.length > index + 1) {
            const nextFrom = slabsArray[index + 1]?.from;
            
            if (livePrevTo !== undefined && livePrevTo !== "") {
              const newFrom = Number(livePrevTo) + 1;
              if (newFrom !== Number(nextFrom)) {
                form.setValue(`slabs.${index + 1}.from`, newFrom, { 
                  shouldValidate: true,
                  shouldDirty: true
                });
              }
            }
          }
        }, 0);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const defaultItemFactory = (currentFields: any[]) => {
    const lastSlab = currentFields.length > 0 ? currentFields[currentFields.length - 1] : null;
    
    let newFrom: number | string = 0;
    if (lastSlab && lastSlab.to !== undefined && lastSlab.to !== "") {
      newFrom = Number(lastSlab.to) + 1;
    }
    
    return {
      from: newFrom,
      to: "",
      type: "variable",
      rate: 0,
      unit: unit || (lastSlab ? lastSlab.unit : "")
    };
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const isValid = await form.trigger();
      let hasManualError = false;
      
      const slabs = form.getValues("slabs") || [];
      slabs.forEach((slab: any, idx: number) => {
        const isLast = idx === slabs.length - 1;
        if (!isLast && (slab.to === "" || slab.to === undefined || slab.to === null)) {
          form.setError(`slabs.${idx}.to`, { type: "manual", message: "Please enter a valid number" });
          hasManualError = true;
        }
      });

      if (isValid && !hasManualError) {
        return form.getValues();
      }
      return null;
    },
    isDirty: () => Object.keys(form.formState.dirtyFields).length > 0
  }));

  return (
    <div className="space-y-4">
            {!hideHeader && (
         <PageHeader title={'Slab Builder'} subtitle='Define inclining or declining block rates. Each slab can charge a flat amount (lifeline tier) or a per-unit variable rate.'
         ></PageHeader>)}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-5 text-sm">
        {slabFieldInfo.map((item) => (
          <div key={item.title}>
            <span className="font-semibold text-foreground">
              {item.title}
            </span>
            <span className="text-muted-foreground text-xs">
              {" "}
              — {item.description}
            </span>
          </div>
          ))}
      </div>
      <SharedFormArray
        name="slabs"
        form={form}
        itemFields={slabFields}
        variant="table"
        emptyMessage="No slabs configured yet."
        addButtonText="Add Slab"
        rowIndexName='Slab'
        minItems={1}
        defaultItemFactory={defaultItemFactory}
      />
    </div>
  );
});

SlabBased.displayName = "SlabBased";
